import type {
  InventoryResponse,
  InventoryRoom,
} from 'app/typings/core/extranet/inventory/inventory';
import { formatDateWithoutHour } from 'app/utils/dates';
import {
  eachDayOfInterval,
  isAfter,
  isBefore,
  startOfDay,
} from 'app/utils/dayjs';

type InventoryRoomOpeningStatus = 'PUBLISHED' | 'UNPUBLISHED' | 'CLOSED';

type RoomOpeningMessage =
  | 'PUBLISHED'
  | 'CLOSED_ON_CM'
  | 'CLOSED_BY_STAYCATION'
  | 'STOP_SELL'
  | 'MISSING_STOCK'
  | 'MISSING_PRICE'
  | 'MISSING_DISCOUNT_PRICE'
  | 'DISCOUNT_PRICE_TOO_LOW'
  | 'PAST_SALES'
  | 'FUTURE_SALES';

type RoomOpeningStatusAndMessage = {
  inventoryStatus: InventoryRoomOpeningStatus;
  message: RoomOpeningMessage;
};

export type RoomPackDetails = {
  packId: number;
  totalPrice?: number;
  discountedTotalPrice?: number;
  discount?: number;
};

export type RoomOpeningInventory = RoomOpeningStatusAndMessage &
  InventoryRoom & {
    remainingStock?: number;
    discount?: number;
    roomId: number;
    packsDetails?: RoomPackDetails[];
  };

// Determines room opening status and message based on sales window, stock, pricing, and closure reasons
const getRoomOpeningStatusAndMessage = (
  isPastSales: boolean,
  isFutureSales: boolean,
  isPriceEmpty: boolean,
  isDiscountPriceEmpty: boolean,
  isDiscountPriceTooLow: boolean,
  hasChannelManager: boolean,
  hasAutoInventory: boolean,
  isClosedByHotel?: boolean,
  isClosedByStaycation?: boolean,
  remainingStock?: number
): RoomOpeningStatusAndMessage => {
  // Check sales window constraints first
  if (isPastSales) {
    return { inventoryStatus: 'CLOSED', message: 'PAST_SALES' };
  }

  if (isFutureSales) {
    return { inventoryStatus: 'CLOSED', message: 'FUTURE_SALES' };
  }

  // Check closure reasons
  if (isClosedByHotel !== undefined && isClosedByHotel) {
    if (hasChannelManager) {
      return { inventoryStatus: 'CLOSED', message: 'CLOSED_ON_CM' };
    } else if (hasAutoInventory) {
      return { inventoryStatus: 'CLOSED', message: 'STOP_SELL' };
    }

    return { inventoryStatus: 'CLOSED', message: 'CLOSED_BY_STAYCATION' };
  }

  if (isClosedByStaycation !== undefined && isClosedByStaycation) {
    return { inventoryStatus: 'UNPUBLISHED', message: 'CLOSED_BY_STAYCATION' };
  }

  // Check stock availability
  if (
    remainingStock === undefined ||
    remainingStock === null ||
    remainingStock === 0
  ) {
    return { inventoryStatus: 'UNPUBLISHED', message: 'MISSING_STOCK' };
  }

  // Check pricing availability
  if (isPriceEmpty) {
    return { inventoryStatus: 'UNPUBLISHED', message: 'MISSING_PRICE' };
  }

  if (isDiscountPriceEmpty) {
    return {
      inventoryStatus: 'UNPUBLISHED',
      message: 'MISSING_DISCOUNT_PRICE',
    };
  }

  if (isDiscountPriceTooLow) {
    return {
      inventoryStatus: 'UNPUBLISHED',
      message: 'DISCOUNT_PRICE_TOO_LOW',
    };
  }

  return { inventoryStatus: 'PUBLISHED', message: 'PUBLISHED' };
};

// Computes room opening inventory for a single date
export const computeRoomOnSingleDate = (
  saleEndDate: Date,
  date: Date,
  roomId: number,
  inventoryRoom: InventoryRoom,
  hasChannelManager: boolean,
  hasAutoInventory: boolean,
  // If we come from an update, we already have a discount value, so we should not recompute it sometimes to avoid double rounding issues
  inputDiscount?: number | null
): RoomOpeningInventory => {
  const isPastSales = isBefore(startOfDay(date), startOfDay(new Date()));
  const isFutureSales = isAfter(
    startOfDay(date),
    startOfDay(new Date(saleEndDate))
  );

  const statusAndMessage = getRoomOpeningStatusAndMessage(
    isPastSales,
    isFutureSales,
    inventoryRoom.price === undefined,
    inventoryRoom.discountPrice === undefined,
    inventoryRoom.discountPrice !== undefined &&
      inventoryRoom.price !== undefined &&
      inventoryRoom.discountPrice > inventoryRoom.price,
    hasChannelManager,
    hasAutoInventory,
    inventoryRoom.isClosedByHotel,
    inventoryRoom.isClosedByStaycation,
    inventoryRoom.remainingStock
  );

  const discount =
    inputDiscount !== undefined
      ? inputDiscount ?? undefined
      : inventoryRoom.price !== undefined &&
        inventoryRoom.discountPrice !== undefined
      ? inventoryRoom.price === 0
        ? 0
        : 1 - inventoryRoom.discountPrice / inventoryRoom.price
      : undefined;

  return {
    ...statusAndMessage,
    ...inventoryRoom,
    roomId,
    discount,
  };
};

// Filters room openings to only include those for a specific date
const filterRoomOpeningsForDate = (
  inventoryInput: InventoryResponse,
  date: Date
): { [roomId: number]: InventoryRoom } => {
  const dateKey = formatDateWithoutHour(date);

  return inventoryInput[dateKey]?.rooms ?? {};
};

// Computes room openings for all rooms on a specific date
const computeRoomsOnDate = (
  saleEndDate: Date,
  date: Date,
  inventoryRooms: { [roomId: number]: InventoryRoom },
  hasChannelManager: boolean,
  hasAutoInventory: boolean
): Record<number, RoomOpeningInventory> => {
  return Object.entries(inventoryRooms).reduce((result, [roomId, room]) => {
    result[parseInt(roomId)] = computeRoomOnSingleDate(
      saleEndDate,
      date,
      parseInt(roomId),
      room,
      hasChannelManager,
      hasAutoInventory
    );

    return result;
  }, {} as Record<number, RoomOpeningInventory>);
};

// Main function: computes room openings for a date range
export const computeRooms = (
  saleEndDate: string,
  startDate: string,
  endDate: string,
  inventoryInput: InventoryResponse,
  hasChannelManager: boolean,
  hasAutoInventory: boolean
): Record<string, Record<number, RoomOpeningInventory>> => {
  const dates = eachDayOfInterval({
    start: new Date(startDate),
    end: new Date(endDate),
  });

  const result = dates.reduce((acc, date) => {
    const dateKey = formatDateWithoutHour(date);
    const openingsForDate = filterRoomOpeningsForDate(inventoryInput, date);

    acc[dateKey] = computeRoomsOnDate(
      new Date(saleEndDate),
      date,
      openingsForDate,
      hasChannelManager,
      hasAutoInventory
    );

    return acc;
  }, {} as Record<string, Record<number, RoomOpeningInventory>>);

  return result;
};
